import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { siteGetSites, accountRegister } from '../../api';
import { SmartField_Api_Contracts_Account_AccountCreateDto, SmartField_Authorization_Contracts_ClaimDto } from '../../api/types.gen';
import { useIsAdmin, useAffiliatedSiteIds } from '../../lib/auth';
import { SmartFieldClaimType } from '../../lib/auth/types';

interface SitePermissions {
  siteId: number;
  siteName: string;
  userManager: SmartFieldClaimType;
  siteManager: SmartFieldClaimType;
  rawData: SmartFieldClaimType;
  fluxData: SmartFieldClaimType;
}

export function RegisterPage() {
  const isAdmin = useIsAdmin();
  const userSiteIds = useAffiliatedSiteIds();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    registerAsAdmin: false,
  });

  const [activeTab, setActiveTab] = useState<number>(0);
  const [sitePermissions, setSitePermissions] = useState<Map<number, SitePermissions>>(new Map());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: sites, isLoading: sitesLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => siteGetSites(),
  });

  // Admins can see all sites, regular users only see their affiliated sites
  const userSites = useMemo(
    () => (isAdmin ? sites : sites?.filter(site => site.id && userSiteIds.includes(site.id))),
    [isAdmin, sites, userSiteIds]
  );

  useEffect(() => {
    if (userSites && userSites.length > 0) {
      const initialPermissions = new Map<number, SitePermissions>();
      userSites.forEach(site => {
        if (site.id) {
          initialPermissions.set(site.id, {
            siteId: site.id,
            siteName: site.name || '',
            userManager: SmartFieldClaimType.None,
            siteManager: SmartFieldClaimType.None,
            rawData: SmartFieldClaimType.None,
            fluxData: SmartFieldClaimType.None,
          });
        }
      });
      setSitePermissions(initialPermissions);
      setActiveTab(userSites[0]?.id || 0);
    }
  }, [userSites]);

  const registerMutation = useMutation({
    mutationFn: (data: SmartField_Api_Contracts_Account_AccountCreateDto) => 
      accountRegister({ requestBody: data }),
    onSuccess: () => {
      setSuccessMessage(`User ${formData.email} has been successfully registered!`);
      setTimeout(() => {
        setSuccessMessage(null);
        setFormData({ fullName: '', email: '', registerAsAdmin: false });
        // Reset permissions
        const resetPermissions = new Map<number, SitePermissions>();
        userSites?.forEach(site => {
          if (site.id) {
            resetPermissions.set(site.id, {
              siteId: site.id,
              siteName: site.name || '',
              userManager: SmartFieldClaimType.None,
              siteManager: SmartFieldClaimType.None,
              rawData: SmartFieldClaimType.None,
              fluxData: SmartFieldClaimType.None,
            });
          }
        });
        setSitePermissions(resetPermissions);
      }, 3000);
    },
  });

  const buildClaims = (): SmartField_Authorization_Contracts_ClaimDto[] => {
    const claims: SmartField_Authorization_Contracts_ClaimDto[] = [];

    if (formData.registerAsAdmin) {
      claims.push({
        type: 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
        value: 'Admin',
      });
    }

    sitePermissions.forEach((perms) => {
      if (perms.userManager !== SmartFieldClaimType.None) {
        claims.push({
          type: perms.userManager,
          value: perms.siteId.toString(),
        });
      }
      if (perms.siteManager !== SmartFieldClaimType.None) {
        claims.push({
          type: perms.siteManager,
          value: perms.siteId.toString(),
        });
      }
      if (perms.rawData !== SmartFieldClaimType.None) {
        claims.push({
          type: perms.rawData,
          value: perms.siteId.toString(),
        });
      }
      if (perms.fluxData !== SmartFieldClaimType.None) {
        claims.push({
          type: perms.fluxData,
          value: perms.siteId.toString(),
        });
      }
    });

    return claims;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const claims = buildClaims();
    const registerRequest: SmartField_Api_Contracts_Account_AccountCreateDto = {
      fullName: formData.fullName,
      email: formData.email,
      claims,
    };

    registerMutation.mutate(registerRequest);
  };

  const updateSitePermission = (
    siteId: number,
    field: keyof Omit<SitePermissions, 'siteId' | 'siteName'>,
    value: SmartFieldClaimType
  ) => {
    setSitePermissions(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(siteId);
      if (existing) {
        newMap.set(siteId, { ...existing, [field]: value });
      }
      return newMap;
    });
  };

  if (sitesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-smartfield mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading sites...</p>
        </div>
      </div>
    );
  }

  if (!userSites || userSites.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-yellow-900">No Sites Available</h3>
              <p className="mt-1 text-sm text-yellow-700">
                No sites available for registration. If you believe this is an error, please refresh the page after a few seconds.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeSitePermissions = sitePermissions.get(activeTab);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Create a new account</h2>

        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        )}

        {registerMutation.error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Registration failed</p>
              <p className="mt-1 text-sm text-red-700">{String(registerMutation.error)}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1.5">
              Username
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              required
              minLength={3}
              maxLength={256}
              pattern="^[a-zA-Z]+$"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              onInvalid={(e) =>
                e.currentTarget.setCustomValidity('Username must contain letters only and be 3–256 characters long.')
              }
              onInput={(e) => e.currentTarget.setCustomValidity('')}
              className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-smartfield focus:border-transparent transition-all bg-white text-gray-900"
              placeholder="JohnDoe"
            />
            <p className="mt-1 text-xs text-gray-500">Letters only, 3-256 characters</p>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-smartfield focus:border-transparent transition-all bg-white text-gray-900"
              placeholder="name@example.com"
            />
          </div>

          <hr className="my-6" />
          <h3 className="text-lg font-semibold text-gray-900">Permissions</h3>

          {isAdmin && (
            <div className="flex items-center">
              <input
                id="registerAsAdmin"
                name="registerAsAdmin"
                type="checkbox"
                checked={formData.registerAsAdmin}
                onChange={(e) => setFormData({ ...formData, registerAsAdmin: e.target.checked })}
                className="h-5 w-5 text-smartfield focus:ring-smartfield border-gray-300 rounded bg-white"
              />
              <label htmlFor="registerAsAdmin" className="ml-3 block text-sm font-medium text-gray-700">
                Register User as Administrator
              </label>
            </div>
          )}

          {/* Site tabs */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
              {userSites.map((site) => (
                <button
                  key={site.id}
                  type="button"
                  onClick={() => setActiveTab(site.id!)}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === site.id
                      ? 'bg-white text-smartfield border-b-2 border-smartfield'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {site.name}
                </button>
              ))}
            </div>

            {activeSitePermissions && (
              <div className="p-6 space-y-6">
                {/* User Management */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3">User Management</h4>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`userManager-${activeTab}`}
                        checked={activeSitePermissions.userManager === SmartFieldClaimType.UserManager}
                        onChange={() => updateSitePermission(activeTab, 'userManager', SmartFieldClaimType.UserManager)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">User Manager</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`userManager-${activeTab}`}
                        checked={activeSitePermissions.userManager === SmartFieldClaimType.None}
                        onChange={() => updateSitePermission(activeTab, 'userManager', SmartFieldClaimType.None)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">None</span>
                    </label>
                  </div>
                </div>

                {/* Site Management */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3">Site Management</h4>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`siteManager-${activeTab}`}
                        checked={activeSitePermissions.siteManager === SmartFieldClaimType.SiteManager}
                        onChange={() => updateSitePermission(activeTab, 'siteManager', SmartFieldClaimType.SiteManager)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Site Manager</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`siteManager-${activeTab}`}
                        checked={activeSitePermissions.siteManager === SmartFieldClaimType.None}
                        onChange={() => updateSitePermission(activeTab, 'siteManager', SmartFieldClaimType.None)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">None</span>
                    </label>
                  </div>
                </div>

                {/* Raw Data Access */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3">Raw Data Access</h4>
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`rawData-${activeTab}`}
                        checked={activeSitePermissions.rawData === SmartFieldClaimType.RawDataEditor}
                        onChange={() => updateSitePermission(activeTab, 'rawData', SmartFieldClaimType.RawDataEditor)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Editor</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`rawData-${activeTab}`}
                        checked={activeSitePermissions.rawData === SmartFieldClaimType.RawDataExporter}
                        onChange={() => updateSitePermission(activeTab, 'rawData', SmartFieldClaimType.RawDataExporter)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Exporter</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`rawData-${activeTab}`}
                        checked={activeSitePermissions.rawData === SmartFieldClaimType.RawDataReader}
                        onChange={() => updateSitePermission(activeTab, 'rawData', SmartFieldClaimType.RawDataReader)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Reader</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`rawData-${activeTab}`}
                        checked={activeSitePermissions.rawData === SmartFieldClaimType.None}
                        onChange={() => updateSitePermission(activeTab, 'rawData', SmartFieldClaimType.None)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">None</span>
                    </label>
                  </div>
                </div>

                {/* Flux Data Access */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3">Flux Data Access</h4>
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`fluxData-${activeTab}`}
                        checked={activeSitePermissions.fluxData === SmartFieldClaimType.FluxDataEditor}
                        onChange={() => updateSitePermission(activeTab, 'fluxData', SmartFieldClaimType.FluxDataEditor)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Editor</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`fluxData-${activeTab}`}
                        checked={activeSitePermissions.fluxData === SmartFieldClaimType.FluxDataExporter}
                        onChange={() => updateSitePermission(activeTab, 'fluxData', SmartFieldClaimType.FluxDataExporter)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Exporter</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`fluxData-${activeTab}`}
                        checked={activeSitePermissions.fluxData === SmartFieldClaimType.FluxDataReader}
                        onChange={() => updateSitePermission(activeTab, 'fluxData', SmartFieldClaimType.FluxDataReader)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">Reader</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`fluxData-${activeTab}`}
                        checked={activeSitePermissions.fluxData === SmartFieldClaimType.None}
                        onChange={() => updateSitePermission(activeTab, 'fluxData', SmartFieldClaimType.None)}
                        className="h-4 w-4 text-smartfield focus:ring-smartfield border-gray-300 bg-white"
                      />
                      <span className="ml-2 text-sm text-gray-700">None</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={registerMutation.isPending}
            className="w-full py-3 px-4 bg-smartfield hover:bg-smartfield-700 text-white font-medium rounded-lg shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {registerMutation.isPending ? 'Registering...' : 'Register'}
          </button>
        </form>
      </div>
    </div>
  );
}
